import { useLocalizationContext, useTranslate } from '@im/react-localization';
import { NavItem } from '@im/ui-lib/nav';
import { useIsAuthenticated } from 'mf_core/auth';
import { useSiteConfig } from 'mf_core/config';
import { useMemo } from 'react';
import { useMyBusinessNavItems } from './useMyBusinessNavItems';
import { useProductsNavItems } from './useProductsNavItems';
import { useServicesNavItems } from './useServicesNavItems';
import { useSolutionsNavItems } from './useSolutionsNavItems';
import { useVendorsNavItems } from './useVendorsNavItems';
import { applicationCenterAvailability } from './utils';
import { useFeatureFlagBase } from '../../hooks/use-feature-flag-base';

const HIDE_SEARCHBAR_PRODUCT_VENDORS_COUNTRIES: Array<string> = ['br'];
export function useSideNavItems(enabled = true): NavItem[] {
  const { locale } = useLocalizationContext();
  const { siteCode } = useSiteConfig();
  const availableInCountry = applicationCenterAvailability({ siteCode });
  const translate = useTranslate();
  const isAuthenticated = useIsAuthenticated();
  const myBusinessNavItems = useMyBusinessNavItems();
  const productsNavItems = useProductsNavItems(locale, enabled);
  const vendorsNavItems = useVendorsNavItems(locale, enabled);
  const solutionsNavItems = useSolutionsNavItems(enabled);
  const servicesNavItems = useServicesNavItems(enabled);
  const { boValues: value, isReady } = useFeatureFlagBase();
  const isLeftNavEnabled =
    isReady && value['im-marketing-customerfeedback-menu-isenabled'] === 'true';
  const isX4ECStorefrontEnabled =
    isReady && value['im-customer-resellermarketplace-isenabled'] === 'true';
  return useMemo<NavItem[]>(() => {
    const navItems: NavItem[] = [];

    const hideSearchBarProductVendorNav =
      HIDE_SEARCHBAR_PRODUCT_VENDORS_COUNTRIES.includes(siteCode) &&
      !isAuthenticated;

    if (isAuthenticated) {
      navItems.push({
        key: 'home',
        label: translate('Navigation.Home'),
        route: '/my/dashboard',
      });
      navItems.push({
        key: 'myBusiness',
        label: translate('Navigation.MyBusiness'),
        navItems: myBusinessNavItems,
      });
    } else {
      navItems.push({
        key: 'home',
        label: translate('Navigation.Home'),
        route: '/home',
      });
    }

    if (!hideSearchBarProductVendorNav) {
      navItems.push({
        key: 'products',
        label: translate('Navigation.Products'),
        navItems: productsNavItems,
      });

      navItems.push({
        key: 'vendors',
        label: translate('Navigation.Vendors'),
        navItems: vendorsNavItems,
      });
    }
    navItems.push({
      key: 'solutions',
      label: translate('Navigation.Solutions'),
      navItems: solutionsNavItems,
    });

    navItems.push({
      key: 'services',
      label: translate('Navigation.Services'),
      navItems: servicesNavItems,
    });

    navItems.push({
      key: 'resources',
      label: translate('Navigation.Resources'),
      route: '/cms/resources',
    });

    if (isAuthenticated) {
      navItems.push({
        key: 'events',
        label: translate('Navigation.Events'),
        route: '/cms/events/landing',
      });
    }
    if (availableInCountry) {
      navItems.push({
        key: 'integrationshub',
        label: translate('AppStore.AppStoreName') || 'Integrations Hub',
        route: isAuthenticated
          ? '/my/integrationshub/appstodownload'
          : '/integrationshub/appstodownload',
      });
    }

    if (isAuthenticated && isLeftNavEnabled)
      navItems.push({
        key: 'feedback',
        label: translate('Navigation.Feedback'),
        onClick: () => (window as any).openFeedbackForm?.(),
      });

    if (isAuthenticated && isX4ECStorefrontEnabled)
      navItems.push({
        key: 'storefrontManagement',
        label: 'Storefront Management',
        route: '/my/resellermarketplace',
      });
    return navItems;
  }, [
    siteCode,
    isAuthenticated,
    translate,
    solutionsNavItems,
    servicesNavItems,
    availableInCountry,
    isLeftNavEnabled,
    isX4ECStorefrontEnabled,
    myBusinessNavItems,
    productsNavItems,
    vendorsNavItems,
  ]);
}
